/**
 * 1.7.3 storyline editing (docs/dev/editor-1.7.3-build.md): V1 as clips at absolute record times, and back.
 *
 * The document keeps V1 as an ordered list where each clip's place follows from the clips before it (its gap before,
 * its transition in). Free placement is easier on absolute times: toTimeline lays V1 out, an op moves, trims or
 * removes items, and fromTimeline writes the list back with gapBeforeTicks. ops.placeVideo, liftClips, rippleDelete
 * and trimClipNoRipple (sequence-ops.ts) are built on these two.
 *
 * The rules of fromTimeline:
 *  - items are taken in order of their start (items starting together keep their order);
 *  - the space before an item is its gapBeforeTicks (left out when 0); a gap after the last clip cannot be kept, so
 *    it is dropped (the storyline ends with its last clip);
 *  - a clip that starts after a gap has no transition into it (transitionIn is cleared; there is nothing to
 *    dissolve from), which also clears a stale transition under a gap;
 *  - a clip keeps its transition only when it overlaps the clip before it by exactly the transition as it applies
 *    (layoutVideo's clamp); touching the clip before it, its transition is cleared (it would pull the clip earlier);
 *  - a clip that overlaps the one before it by more than that is placed right after it (ops never ask for this;
 *    it keeps fromTimeline total).
 * toTimeline then fromTimeline gives back the same clips (the same objects where nothing changed), so a document
 * without edits keeps its bytes.
 */
import { clipDuration, gapBefore, layoutVideo, maxTransitionTicks, snap, type SequenceDocument, type VideoClip } from './sequence.ts';
import type { Rational } from './time.ts';

/** A storyline clip at its absolute record time. `end` is start plus the clip's length. */
export interface TimelineItem {
  clip: VideoClip;
  start: number;
  end: number;
}

/** V1 at absolute record times, in storyline order (layoutVideo's recordIn and recordOut). */
export function toTimeline(doc: SequenceDocument): TimelineItem[] {
  return layoutVideo(doc).map((p) => ({ clip: p.clip, start: p.recordIn, end: p.recordOut }));
}

/** The transition into `clip` from `prev` as it would apply (layoutVideo's clamp), in ticks; 0 for none. */
function appliedTransition(prev: VideoClip, clip: VideoClip, rate: Rational): number {
  const want = clip.transitionIn?.durationTicks ?? 0;
  return want > 0 ? Math.min(snap(want, rate), maxTransitionTicks(prev, clip, rate)) : 0;
}

/** A clip with a gap of `gap` ticks before it (kept as it was when that is the same gap on the frame grid). */
function withGap(clip: VideoClip, gap: number, rate: Rational): VideoClip {
  if (gapBefore(clip, rate) === gap) return clip;
  const next = { ...clip };
  if (gap > 0) next.gapBeforeTicks = gap; else delete next.gapBeforeTicks;
  return next;
}

/**
 * The storyline clips for items at absolute record times (the rules above). Starts are taken as given (callers
 * snap them to the frame grid); a start before 0 counts as 0.
 */
export function fromTimeline(items: readonly TimelineItem[], rate: Rational): VideoClip[] {
  const sorted = items.map((it, i) => ({ it, i })).sort((a, b) => a.it.start - b.it.start || a.i - b.i).map((x) => x.it);
  const out: VideoClip[] = [];
  let prevEnd = 0;
  for (const it of sorted) {
    const start = Math.max(0, it.start);
    const prev = out.at(-1);
    let clip = it.clip;
    if (!prev) {
      // The first clip: a transition on it is ignored (layoutVideo), so it is left as it is.
      clip = withGap(clip, start, rate);
      prevEnd = start;
    } else {
      const applied = appliedTransition(prev, clip, rate);
      if (applied > 0 && start === prevEnd - applied) {
        clip = withGap(clip, 0, rate);
        prevEnd -= applied;
      } else {
        // A gap, a cut, or (defensively) an overlap: placed after the clip before it, with no transition into it
        // when there is a gap or the transition would apply. One that applies as 0 ticks is left as it is.
        const gap = Math.max(0, start - prevEnd);
        clip = withGap(clip, gap, rate);
        if (clip.transitionIn && (gap > 0 || applied > 0)) clip = { ...clip, transitionIn: null };
        prevEnd += gap;
      }
    }
    prevEnd += clipDuration(clip);
    out.push(clip);
  }
  return out;
}

/** A gap on V1 (1.7.3, selectable as `{ kind: 'gap', id }`): `id` is the clip after it, [start, end) the black. */
export interface StorylineGap {
  id: string;
  start: number;
  end: number;
}

/** Every gap on V1, in order (a gap is always before a clip; there is never one after the last). */
export function gapsOf(doc: SequenceDocument): StorylineGap[] {
  return layoutVideo(doc).filter((p) => p.gapBefore > 0).map((p) => ({ id: p.clip.id, start: p.recordIn - p.gapBefore, end: p.recordIn }));
}

/** The document with V1 written back from `items` (fromTimeline); the same document when nothing changed. */
export function withTimeline(doc: SequenceDocument, items: readonly TimelineItem[]): SequenceDocument {
  const video = fromTimeline(items, doc.frameRate);
  return video.length === doc.video.length && video.every((c, i) => c === doc.video[i]) ? doc : { ...doc, video };
}
